import React from "react";
import AdminLayout from "layouts/Admin.js";
import { StateContext } from "state.js";
import { makeStyles } from "@material-ui/core/styles";
import styles from "assets/jss/pages/PageStyles.js";
import { useGetVictimExport } from "hooks";

// Creative Tim
import GridContainer from "components-material-ui/Grid/GridContainer.js";
import GridItem from "components-material-ui/Grid/GridItem.js";
import Button from "components-material-ui/CustomButtons/Button.js";
import Card from "components-material-ui/Card/Card.js";
import CardHeader from "components-material-ui/Card/CardHeader.js";
import CardIcon from "components-material-ui/Card/CardIcon.js";
import CardBody from "components-material-ui/Card/CardBody.js";
import Danger from "components-material-ui/Typography/Danger.js";

// Material UI
import Box from "@material-ui/core/Box";
import Divider from "@material-ui/core/Divider";
import Typography from "@material-ui/core/Typography";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import FormControl from "@material-ui/core/FormControl";
import FormLabel from "@material-ui/core/FormLabel";
import Radio from "@material-ui/core/Radio";
import RadioGroup from "@material-ui/core/RadioGroup";
import Checkbox from "@material-ui/core/Checkbox";
import CircularProgress from "@material-ui/core/CircularProgress";

// Icons
import GetApp from "@material-ui/icons/GetApp";

const useStyles = makeStyles(styles);

// Column keys MUST match EXPORT_COLUMNS in the Rails VictimsController.
// Grouped for a readable picker; the canonical order below also drives the
// order of columns in the generated CSV.
const COLUMN_GROUPS = [
  {
    group: "Requested",
    columns: [
      { key: "case_number", label: "Case Number" },
      { key: "status", label: "Status" },
      { key: "first_name", label: "First Name" },
      { key: "last_name", label: "Last Name" },
      { key: "age", label: "Age" },
      { key: "gender", label: "Gender" },
      { key: "race", label: "Race" },
      { key: "cause", label: "Cause" },
      { key: "incident_city", label: "Incident City" },
      { key: "incident_state", label: "Incident State" },
      { key: "incident_zip", label: "Incident Zip Code" },
    ],
  },
  {
    group: "Victim",
    columns: [
      { key: "victim_id", label: "Victim ID" },
      { key: "preferred_name", label: "Preferred Name" },
      { key: "full_name", label: "Full Name" },
      { key: "date_of_birth", label: "Date of Birth" },
      { key: "date_of_death", label: "Date of Death" },
      { key: "date_went_missing", label: "Date Went Missing" },
      { key: "city_of_death", label: "City of Death" },
      { key: "state_of_death", label: "State of Death" },
      { key: "special_category", label: "Special Category" },
      { key: "special_categories", label: "Special Categories" },
      { key: "case_spotlight", label: "Case Spotlight" },
      { key: "anniversary_date", label: "Anniversary Date" },
      { key: "exclude_from_public_view", label: "Exclude From Public View" },
      { key: "victim_is_deleted", label: "Victim Archived" },
      { key: "victim_deleted_date", label: "Victim Archived Date" },
      { key: "victim_created_at", label: "Victim Created At" },
      { key: "victim_updated_at", label: "Victim Updated At" },
    ],
  },
  {
    group: "Case",
    columns: [
      { key: "case_id", label: "Case ID" },
      { key: "investigative_case_number", label: "Investigative Case Number" },
      { key: "law_enforcement_agency", label: "Law Enforcement Agency" },
      { key: "submitted_by_law_enforcement", label: "Submitted By Law Enforcement" },
      { key: "how_submitted", label: "How Submitted" },
      { key: "reported_date", label: "Reported Date" },
      { key: "submitted_date", label: "Submitted Date" },
      { key: "met_media", label: "Met Media" },
      { key: "news_outlet", label: "News Outlet" },
      { key: "social_media_post_date", label: "Social Media Post Date" },
      { key: "written_spotlight_date", label: "Written Spotlight Date" },
      { key: "fb_live_feature_date", label: "FB Live Feature Date" },
      { key: "on_tv_date", label: "On TV Date" },
      { key: "case_is_deleted", label: "Case Archived" },
      { key: "case_deleted_date", label: "Case Archived Date" },
      { key: "case_created_at", label: "Case Created At" },
      { key: "case_updated_at", label: "Case Updated At" },
    ],
  },
  {
    group: "Incident",
    columns: [
      { key: "incident_id", label: "Incident ID" },
      { key: "incident_address1", label: "Incident Address 1" },
      { key: "incident_address2", label: "Incident Address 2" },
      { key: "incident_county", label: "Incident County" },
      { key: "incident_country", label: "Incident Country" },
      { key: "incident_offense", label: "Incident Offense" },
      { key: "incident_description", label: "Incident Description" },
      { key: "incident_is_description_public", label: "Incident Description Public" },
      { key: "incident_suspect", label: "Incident Suspect" },
      { key: "incident_date", label: "Incident Date" },
      { key: "incident_is_deleted", label: "Incident Archived" },
      { key: "incident_created_at", label: "Incident Created At" },
      { key: "incident_updated_at", label: "Incident Updated At" },
    ],
  },
];

// Canonical ordered list of every column key.
const ALL_COLUMN_KEYS = COLUMN_GROUPS.reduce(
  (keys, group) => keys.concat(group.columns.map((column) => column.key)),
  []
);

// Selected by default: the columns the client originally requested.
const DEFAULT_COLUMNS = COLUMN_GROUPS[0].columns.map((column) => column.key);

function Page () {
  const classes = useStyles();
  const State = React.useContext(StateContext);
  const [token] = React.useState(State?.user?.value?.token);

  const [selected, setSelected] = React.useState(DEFAULT_COLUMNS);
  const [includeArchived, setIncludeArchived] = React.useState(false);
  const [exportFormat, setExportFormat] = React.useState("csv");
  const [shouldDownload, setShouldDownload] = React.useState(false);

  const { isCompleted, downloadError, isDownloading } = useGetVictimExport(
    token,
    selected,
    includeArchived,
    exportFormat,
    shouldDownload
  );

  // Reset the trigger once a download finishes so it can be run again.
  React.useEffect(() => {
    if (isCompleted || downloadError) {
      setShouldDownload(false);
    }
  }, [isCompleted, downloadError]);

  const toggleColumn = (key) => {
    setSelected((current) =>
      current.includes(key)
        ? current.filter((item) => item !== key)
        : [...current, key]
    );
  };

  const toggleGroup = (group, allSelected) => {
    const groupKeys = group.columns.map((column) => column.key);
    setSelected((current) =>
      allSelected
        ? current.filter((key) => !groupKeys.includes(key))
        : Array.from(new Set([...current, ...groupKeys]))
    );
  };

  const selectAll = () => setSelected(ALL_COLUMN_KEYS);
  const clearAll = () => setSelected([]);
  const resetToRequested = () => setSelected(DEFAULT_COLUMNS);

  // Preserve the canonical column order regardless of toggle order.
  const orderedSelection = () =>
    ALL_COLUMN_KEYS.filter((key) => selected.includes(key));

  const handleDownload = () => {
    const ordered = orderedSelection();
    if (ordered.length === 0) return;
    setSelected(ordered);
    setShouldDownload(true);
  };

  return (
    <GridContainer>
      <GridItem xs={12} sm={12} md={10}>
        <Card>
          <CardHeader color="info" icon>
            <CardIcon color="info">
              <GetApp />
            </CardIcon>
            <h4 className={classes.cardIconTitle}>Export Victims</h4>
          </CardHeader>
          <CardBody>
            <p>
              Choose the columns to include, then download a CSV or Excel file of
              all active victims. Victim, case, and incident fields are all
              available.
            </p>

            <Box mb={2}>
              <Button size="sm" color="info" onClick={selectAll}>
                Select All
              </Button>
              <Button size="sm" onClick={clearAll}>
                Clear All
              </Button>
              <Button size="sm" onClick={resetToRequested}>
                Requested Only
              </Button>
            </Box>

            {COLUMN_GROUPS.map((group) => {
              const groupKeys = group.columns.map((column) => column.key);
              const allSelected = groupKeys.every((key) => selected.includes(key));
              const someSelected = groupKeys.some((key) => selected.includes(key));

              return (
                <Box key={group.group} mb={2}>
                  <Box display="flex" alignItems="center">
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={allSelected}
                          indeterminate={someSelected && !allSelected}
                          onChange={() => toggleGroup(group, allSelected)}
                          color="primary"
                        />
                      }
                      label={
                        <Typography variant="subtitle1">
                          <strong>{group.group}</strong>
                        </Typography>
                      }
                    />
                  </Box>
                  <Divider />
                  <GridContainer>
                    {group.columns.map((column) => (
                      <GridItem xs={12} sm={6} md={4} key={column.key}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={selected.includes(column.key)}
                              onChange={() => toggleColumn(column.key)}
                              color="primary"
                            />
                          }
                          label={column.label}
                        />
                      </GridItem>
                    ))}
                  </GridContainer>
                </Box>
              );
            })}

            <Box mt={2}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={includeArchived}
                    onChange={(event) => setIncludeArchived(event.target.checked)}
                    color="primary"
                  />
                }
                label="Include archived victims"
              />
            </Box>

            <Box mt={2}>
              <FormControl component="fieldset">
                <FormLabel component="legend">File format</FormLabel>
                <RadioGroup
                  row
                  name="export-format"
                  value={exportFormat}
                  onChange={(event) => setExportFormat(event.target.value)}
                >
                  <FormControlLabel value="csv" control={<Radio color="primary" />} label="CSV" />
                  <FormControlLabel value="xlsx" control={<Radio color="primary" />} label="Excel (.xlsx)" />
                </RadioGroup>
              </FormControl>
            </Box>

            <Box mt={2}>
              <Button
                color="info"
                onClick={handleDownload}
                disabled={selected.length === 0 || isDownloading}
              >
                <GetApp /> {exportFormat === "xlsx" ? "Export to Excel" : "Export to CSV"}
              </Button>
            </Box>

            {selected.length === 0 && (
              <Box mt={1}>
                <Danger>Select at least one column to export.</Danger>
              </Box>
            )}

            {isDownloading && (
              <Box textAlign="center" pb={5}>
                <p>Preparing export</p>
                <CircularProgress />
              </Box>
            )}

            {downloadError && (
              <Box textAlign="center" pb={5}>
                <Danger>{downloadError.message || "Export failed."}</Danger>
              </Box>
            )}
          </CardBody>
        </Card>
      </GridItem>
    </GridContainer>
  );
}

export default function PageAdminVictimExport () {
  return <AdminLayout component={<Page />} />;
}
